"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import axios from "axios"
import loginVector from "../Assets/login-vector.png"
import logo from "../Assets/logo.png"
import { FaEye, FaEyeSlash, FaUser, FaLock } from "react-icons/fa"
import { initializeApp } from "firebase/app"
import { getAuth, GoogleAuthProvider, signInWithPopup } from "firebase/auth"

const firebaseConfig = {
  apiKey: "AIzaSyAf8kGhfFklzSCrqKYA5m4ULh_fuf2uRxk",
  authDomain: "magna-sight.firebaseapp.com",
  projectId: "magna-sight",
  storageBucket: "magna-sight.firebasestorage.app",
  messagingSenderId: "472439616491",
  appId: "1:472439616491:web:a3a5943c4b40470649dfae",
  measurementId: "G-H9G8VHGYH5"
};

// Initialize Firebase only once
let firebaseApp
if (!window._firebaseInitialized) {
  firebaseApp = initializeApp(firebaseConfig)
  window._firebaseInitialized = true
} else {
  firebaseApp = initializeApp(firebaseConfig)
}
const auth = getAuth(firebaseApp)
const provider = new GoogleAuthProvider()

function LoginPage() {
  const navigate = useNavigate()
  const [username, setUsername] = useState("")
  const [password, setPassword] = useState("")
  const [errorMessage, setErrorMessage] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  
  // MFA states
  const [showMFAPopup, setShowMFAPopup] = useState(false)
  const [mfaCode, setMfaCode] = useState("")
  const [tempToken, setTempToken] = useState("")
  const [mfaError, setMfaError] = useState("")
  const [mfaLoading, setMfaLoading] = useState(false)
  
  // Google MFA states
  const [showGoogleMFAPopup, setShowGoogleMFAPopup] = useState(false)
  const [googleMfaCode, setGoogleMfaCode] = useState("")
  const [googleMfaError, setGoogleMfaError] = useState("")
  const [googleMfaLoading, setGoogleMfaLoading] = useState(false)
  const [googleUserInfo, setGoogleUserInfo] = useState(null)
  const [firebaseToken, setFirebaseToken] = useState("")

  const handlePasswordChange = (e) => {
    setPassword(e.target.value)
  }

  const toggleShowPassword = () => {
    setShowPassword(!showPassword)
  }

  useEffect(() => {
    localStorage.removeItem("token")
  }, [])

  useEffect(() => {
    const token = localStorage.getItem("token")
    if (token) {
      navigate("/dashboard")
    }
  }, [navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setErrorMessage("")
    setIsLoading(true)

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/login`, {
        username,
        password,
      })

      // Check for MFA requirement in successful response
      if (response.data?.detail?.mfa_required) {
        setTempToken(response.data.detail.temp_token || 'mfa_pending')
        setShowMFAPopup(true)
        setMfaError("")  // Clear error, show instruction instead
        return
      }

      if (response.data && response.data.token) {
        const { token, user } = response.data

        localStorage.setItem("token", token)
        localStorage.setItem("user", JSON.stringify(user))

        navigate("/dashboard")
      }
    } catch (error) {
      if (error.response) {
        // Check for MFA requirement in error response
        console.log("Error response data:", error.response.data)
        if (error.response.data?.detail?.mfa_required || 
            error.response.data?.mfa_required ||
            error.response.status === 206) {
          const tempToken = error.response.data?.detail?.temp_token || 
                           error.response.data?.temp_token || 
                           'mfa_pending'
          console.log("MFA required, temp_token:", tempToken)
          setTempToken(tempToken)
          setShowMFAPopup(true)
          setMfaError("")  // Clear error, show instruction instead
          return
        }
        
        switch (error.response.status) {
          case 401:
            setErrorMessage("Invalid username or password")
            break
          case 400:
            setErrorMessage("Bad request. Please check your input.")
            break
          case 403:
            // Pass email from API response to verify-email page
            const email = error.response.data?.detail?.email || username
            navigate("/verify-email", { state: { username: email } })
            return
          case 500:
            setErrorMessage("Server error. Please try again later.")
            break
          default:
            setErrorMessage("An unexpected error occurred. Please try again.")
        }
      } else {
        setErrorMessage("Network error. Please check your connection.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleMFASubmit = async (e) => {
    e.preventDefault()
    // Use the auto-submit function to avoid code duplication
    await handleMFAAutoSubmit(mfaCode)
  }

  const handleMFAInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9\-]/g, '').slice(0, 11)
    setMfaCode(value)
    
    // Auto-submit when 6 digits are entered (for TOTP codes)
    if (value.length === 6 && !value.includes('-') && !mfaLoading) {
      setTimeout(() => {
        handleMFAAutoSubmit(value)
      }, 500)
    }
  }

  const handleMFAAutoSubmit = async (code) => {
    setMfaError("")
    setMfaLoading(true)

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_BASE_URL}/api/users/login`, {
        username,
        password,
        mfa_code: code,
        temp_token: tempToken
      })

      if (response.data && response.data.token) {
        const { token, user } = response.data
        localStorage.setItem("token", token)
        localStorage.setItem("user", JSON.stringify(user))
        navigate("/dashboard")
      }
    } catch (error) {
      if (error.response) {
        setMfaError(error.response.data?.message || error.response.data?.detail || "Invalid MFA code")
      } else {
        setMfaError("Network error. Please try again.")
      }
    } finally {
      setMfaLoading(false)
    }
  }

  const resetMFA = () => {
    setShowMFAPopup(false)
    setMfaCode("")
    setTempToken("")
    setMfaError("")
    setIsLoading(false)
    setMfaLoading(false)
  }

  const handleGoogleMFASubmit = async (code) => {
    setGoogleMfaError("")
    setGoogleMfaLoading(true)

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/google-signin-mfa`,
        {
          firebase_token: firebaseToken,
          mfa_code: code,
          user_id: googleUserInfo.user_id
        }
      )

      if (response.data && response.data.token) {
        const { token, user } = response.data
        localStorage.setItem("token", token)
        localStorage.setItem("user", JSON.stringify(user))
        navigate("/dashboard")
      }
    } catch (error) {
      if (error.response) {
        setGoogleMfaError(error.response.data?.message || error.response.data?.detail || "Invalid MFA code")
      } else {
        setGoogleMfaError("Network error. Please try again.")
      }
    } finally {
      setGoogleMfaLoading(false)
    }
  }

  const handleGoogleMFAInputChange = (e) => {
    const value = e.target.value.replace(/[^0-9\-]/g, '').slice(0, 11)
    setGoogleMfaCode(value)
    
    // Auto-submit when 6 digits are entered (for TOTP codes)
    if (value.length === 6 && !value.includes('-') && !googleMfaLoading) {
      setTimeout(() => {
        handleGoogleMFASubmit(value)
      }, 500)
    }
  }

  const resetGoogleMFA = () => {
    setShowGoogleMFAPopup(false)
    setGoogleMfaCode("")
    setGoogleMfaError("")
    setGoogleUserInfo(null)
    setFirebaseToken("")
    setGoogleMfaLoading(false)
  }

  const handleGoogleLogin = async () => {
    setErrorMessage("")
    setIsLoading(true)
    try {
      // 1. Google sign-in popup
      const result = await signInWithPopup(auth, provider)
      const user = result.user
      // 2. Get Firebase ID Token
      const idToken = await user.getIdToken()
      // 3. Send to backend
      const response = await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/users/google-signin`,
        { firebase_token: idToken }
      )
      
      console.log("Google Sign-in response:", response.data)
      
      // Check if MFA is required
      if (response.data.status === "mfa_required" || response.data.mfa_required) {
        setGoogleUserInfo({
          name: response.data.name,
          email: response.data.email,
          user_id: response.data.user_id
        })
        setFirebaseToken(response.data.firebase_token || idToken)
        setShowGoogleMFAPopup(true)
        setGoogleMfaError("")
        return
      }
      
      // Normal login success
      if (response.data && response.data.token) {
        const { token, user } = response.data
        localStorage.setItem("token", token)
        localStorage.setItem("user", JSON.stringify(user))
        navigate("/dashboard")
      } else {
        setErrorMessage("Google login failed. Please try again.")
      }
    } catch (error) {
      if (error.response) {
        if (error.response.status === 404) {
          const email = error.response.data?.detail?.email || ""
          setErrorMessage(
            `User not registered. Please contact your company administrator to register this email${email ? ` ${email}` : ""} first before using Google Sign-in.`
          )
        } else if (error.response.data && error.response.data.detail) {
          setErrorMessage(error.response.data.detail)
        } else {
          setErrorMessage("Google login failed. Please try again.")
        }
      } else if (error.code === "auth/popup-closed-by-user") {
        setErrorMessage("Google sign-in was cancelled.")
      } else {
        setErrorMessage("Google login failed. Please try again.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-blue-600 to-purple-600"></div>
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='https://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%234F46E5' fillOpacity='0.1'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }}
        ></div>
      </div>

      {/* Floating Elements */}
      <div className="absolute top-40 right-20 w-16 h-16 bg-purple-200 rounded-full opacity-20 animate-pulse delay-1000"></div>

      <div className="relative z-10 min-h-screen flex">
        {/* Left Side - Illustration & Branding */}
        <div className="hidden lg:flex lg:w-1/2 xl:w-3/5 flex-col justify-center items-center p-12 relative">
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10 rounded-r-3xl"></div>

          {/* Content */}
          <div className="relative z-10 text-center max-w-lg">
            {/* Logo */}
            <div>
              <img src={logo || "/placeholder.svg"} alt="MagnaSight Logo" className="h-64 mx-auto" />
              <div className="flex items-center justify-center space-x-2">
              </div>
            </div>

            {/* Illustration */}
            <div className="mb-8">
              <img
                src={loginVector || "/placeholder.svg"}
                alt="Login Illustration"
                className="w-4/5 h-auto object-contain mx-auto drop-shadow-2xl"
              />
            </div>
          </div>
        </div>

        {/* Right Side - Login Form */}
        <div className="w-full lg:w-1/2 xl:w-2/5 flex items-center justify-center p-4 sm:p-6 lg:p-12 min-h-screen py-8 sm:py-12">
          <div className="w-full max-w-md my-auto">
            {/* Mobile Logo */}
            <div className="lg:hidden text-center -mt-3 -mb-3 sm:mb-2">
              <img
                src={logo || "/placeholder.svg"}
                alt="MagnaSight Logo"
                className="w-auto mx-auto object-contain"
                style={{ height: "135px" }}
              />
            </div>

            {/* Login Card */}
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-5 sm:p-8 lg:p-10">
              {/* Header */}
              <div className="text-center mb-5 sm:mb-8">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-800 mb-1.5 sm:mb-2">Sign In</h2>
                <p className="text-xs sm:text-sm text-gray-600">Enter your credentials to access your account</p>
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className="mb-4 sm:mb-6 p-3 sm:p-4 bg-red-50 border border-red-200 rounded-xl">
                  <div className="flex items-center">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 bg-red-100 rounded-full flex items-center justify-center mr-2.5 sm:mr-3 shrink-0">
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-red-500 rounded-full"></div>
                    </div>
                    <p className="text-red-700 text-xs sm:text-sm font-medium">{errorMessage}</p>
                  </div>
                </div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                {/* Username Field */}
                <div>
                  <label className="block text-gray-700 text-xs sm:text-sm font-semibold mb-1.5 sm:mb-2" htmlFor="username">
                    Username
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 sm:pl-4 flex items-center pointer-events-none">
                      <FaUser className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="w-full pl-10 sm:pl-12 pr-4 py-2.5 sm:py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      placeholder="Enter your username"
                      required
                    />
                  </div>
                </div>

                {/* Password Field */}
                <div>
                  <label htmlFor="password" className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5 sm:mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 sm:pl-4 flex items-center pointer-events-none">
                      <FaLock className="h-4 w-4 text-gray-400" />
                    </div>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      className="w-full pl-10 sm:pl-12 pr-11 sm:pr-12 py-2.5 sm:py-3 text-sm border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 bg-gray-50/50 hover:bg-white"
                      value={password}
                      onChange={handlePasswordChange}
                      placeholder="Enter your password"
                      required
                    />
                    <button
                      type="button"
                      className="absolute inset-y-0 right-0 pr-3.5 sm:pr-4 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                      onClick={toggleShowPassword}
                    >
                      {showPassword ? <FaEyeSlash className="h-4 w-4" /> : <FaEye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="space-y-3 sm:space-y-4 pt-1 sm:pt-2">
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-2.5 sm:py-3 px-6 rounded-xl text-sm sm:text-base font-semibold hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-md hover:shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                  >
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Signing in...
                      </div>
                    ) : (
                      "Sign In"
                    )}
                  </button>

                  {/* Divider */}
                  <div className="relative my-2 sm:my-3">
                    <div className="absolute inset-0 flex items-center">
                      <div className="w-full border-t border-gray-200"></div>
                    </div>
                    <div className="relative flex justify-center text-xs sm:text-sm">
                      <span className="px-3 sm:px-4 bg-white text-gray-500 font-medium">Or continue with</span>
                    </div>
                  </div>

                  {/* Google Login */}
                  <button
                    type="button"
                    onClick={handleGoogleLogin}
                    className="w-full flex items-center justify-center py-2.5 sm:py-3 px-6 border border-gray-200 rounded-xl text-gray-700 text-sm sm:text-base font-semibold bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-200 shadow-xs hover:shadow-sm"
                  >
                    <svg className="w-5 h-5 mr-3" fill="currentColor" viewBox="0 0 24 24">
                      <path
                        d="M23.492 12.261c0-.854-.077-1.677-.207-2.48H12v4.705h6.548c-.282 1.434-1.15 2.64-2.43 3.448v2.839h3.905c2.286-2.113 3.569-5.227 3.569-8.512z"
                        fill="#4285F4"
                      />
                      <path
                        d="M12 24c3.237 0 5.944-1.07 7.926-2.898l-3.906-2.839c-1.09.738-2.46 1.179-4.02 1.179-3.088 0-5.698-2.084-6.632-4.887H1.35v3.019A11.994 11.994 0 0 0 12 24z"
                        fill="#34A853"
                      />
                      <path
                        d="M5.368 14.555a7.189 7.189 0 0 1 0-4.59v-3.02H1.35a11.995 11.995 0 0 0 0 10.63l4.018-3.02z"
                        fill="#FBBC05"
                      />
                      <path
                        d="M12 4.76c1.788 0 3.392.616 4.654 1.82l3.43-3.43A11.965 11.965 0 0 0 12 0C7.476 0 3.621 2.688 1.35 6.945l4.018 3.02c.933-2.804 3.543-4.89 6.632-4.89z"
                        fill="#EA4335"
                      />
                    </svg>
                    Sign in with Google
                  </button>
                </div>
              </form>
            </div>

            {/* Footer */}
            <div className="text-center text-gray-500 text-xs sm:text-sm mt-4 sm:mt-8">
              <p>© Smartnet Magna Global {new Date().getFullYear()}</p>
            </div>
          </div>
        </div>
      </div>

      {/* MFA Popup Modal */}
      {showMFAPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto relative animate-in fade-in-0 zoom-in-95 duration-300">
            {/* Close Button */}
            <button
              onClick={resetMFA}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="p-8">
              {/* Header */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Multi-Factor Authentication</h3>
                <p className="text-gray-600 text-sm">Enter the 6-digit code from your authenticator app</p>
              </div>

              {/* MFA Error Message */}
              {mfaError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-red-100 rounded-full flex items-center justify-center mr-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    </div>
                    <p className="text-red-700 text-sm">{mfaError}</p>
                  </div>
                </div>
              )}

              {/* MFA Info Message (when no error) */}
              {!mfaError && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    </div>
                    <p className="text-blue-700 text-sm">Your account has MFA enabled. Please enter your authentication code.</p>
                  </div>
                </div>
              )}

              {/* MFA Form */}
              <form onSubmit={handleMFASubmit} className="space-y-6">
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2">
                    Authentication Code
                  </label>
                  <input
                    type="text"
                    value={mfaCode}
                    onChange={handleMFAInputChange}
                    className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-center text-2xl font-mono tracking-widest bg-gray-50/50 hover:bg-white"
                    placeholder="123456"
                    maxLength={11}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Enter 6-digit code from your authenticator app or backup code (1234-5678)
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={mfaLoading || (!mfaCode || mfaCode.length < 6)}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {mfaLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Verifying...
                      </div>
                    ) : (
                      "Verify Code"
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={resetMFA}
                    className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
                  >
                    Use different account
                  </button>
                </div>
              </form>

              {/* Auto-submit info */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  Code will auto-submit when 6 digits are entered
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Google MFA Popup Modal */}
      {showGoogleMFAPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-auto relative animate-in fade-in-0 zoom-in-95 duration-300">
            {/* Close Button */}
            <button
              onClick={resetGoogleMFA}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors z-10"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            <div className="p-8">
              {/* Header with Google branding */}
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-600" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M23.492 12.261c0-.854-.077-1.677-.207-2.48H12v4.705h6.548c-.282 1.434-1.15 2.64-2.43 3.448v2.839h3.905c2.286-2.113 3.569-5.227 3.569-8.512z"/>
                    <path fill="#34A853" d="M12 24c3.237 0 5.944-1.07 7.926-2.898l-3.906-2.839c-1.09.738-2.46 1.179-4.02 1.179-3.088 0-5.698-2.084-6.632-4.887H1.35v3.019A11.994 11.994 0 0 0 12 24z"/>
                    <path fill="#FBBC05" d="M5.368 14.555a7.189 7.189 0 0 1 0-4.59v-3.02H1.35a11.995 11.995 0 0 0 0 10.63l4.018-3.02z"/>
                    <path fill="#EA4335" d="M12 4.76c1.788 0 3.392.616 4.654 1.82l3.43-3.43A11.965 11.965 0 0 0 12 0C7.476 0 3.621 2.688 1.35 6.945l4.018 3.02c.933-2.804 3.543-4.89 6.632-4.89z"/>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">Complete Sign-in with Google</h3>
                <p className="text-gray-600 text-sm">Your account has MFA enabled for extra security</p>
              </div>

              {/* User Info */}
              {googleUserInfo && (
                <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
                      <span className="text-white font-semibold text-sm">
                        {googleUserInfo.name?.charAt(0)?.toUpperCase() || 'G'}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{googleUserInfo.name}</p>
                      <p className="text-sm text-gray-600">{googleUserInfo.email}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Google MFA Error Message */}
              {googleMfaError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-red-100 rounded-full flex items-center justify-center mr-2">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    </div>
                    <p className="text-red-700 text-sm">{googleMfaError}</p>
                  </div>
                </div>
              )}

              {/* Google MFA Info Message (when no error) */}
              {!googleMfaError && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-4 h-4 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    </div>
                    <p className="text-blue-700 text-sm">Enter the code from your authenticator app to complete sign-in.</p>
                  </div>
                </div>
              )}

              {/* Google MFA Form */}
              <form onSubmit={(e) => { e.preventDefault(); handleGoogleMFASubmit(googleMfaCode); }} className="space-y-6">
                <div>
                  <label className="block text-gray-700 text-sm font-semibold mb-2">
                    Authentication Code
                  </label>
                  <input
                    type="text"
                    value={googleMfaCode}
                    onChange={handleGoogleMFAInputChange}
                    className="w-full px-4 py-4 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 text-center text-2xl font-mono tracking-widest bg-gray-50/50 hover:bg-white"
                    placeholder="123456"
                    maxLength={11}
                    autoFocus
                  />
                  <p className="text-xs text-gray-500 mt-2 text-center">
                    Enter 6-digit code from your authenticator app or backup code (1234-5678)
                  </p>
                </div>

                <div className="space-y-3">
                  <button
                    type="submit"
                    disabled={googleMfaLoading || (!googleMfaCode || googleMfaCode.length < 6)}
                    className="w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 px-6 rounded-xl font-semibold hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-70 disabled:cursor-not-allowed disabled:transform-none"
                  >
                    {googleMfaLoading ? (
                      <div className="flex items-center justify-center">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                        Verifying...
                      </div>
                    ) : (
                      "Verify & Sign In"
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={resetGoogleMFA}
                    className="w-full py-2 px-4 text-gray-600 hover:text-gray-800 text-sm font-medium transition-colors"
                  >
                    Use different account
                  </button>
                </div>
              </form>

              {/* Auto-submit info */}
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-500">
                  Code will auto-submit when 6 digits are entered
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default LoginPage